import { call, takeLatest, put } from "redux-saga/effects";
import { callApi } from "../../util/accountSuperAgentUtil";
import actions from "../actionType";
import { notification } from "../../util/notification";
import { getItem } from "../../libs/encryptedStorage";
import Helper from "../../util/helper";
import bugsnagClient from "@bugsnag/js";

// Recurring payments / autopay saga (Stripe-only, Phase 1). Talks to the backend
// money-spine: hosted card setup, saved cards, subscription enrol / replace-card / retry,
// and the staff needs-attention list. Mirrors the accountPackage saga style.

function getLoggedInData(loggedInData: any) {
  if (!loggedInData || !loggedInData.user || !loggedInData.user.id) {
    const centerId = getItem("centerId") || Helper.getUrlParam("centerId");
    return { centerId, user: { name: "" } };
  }
  return loggedInData;
}

function fail(payload: any = {}) {
  return { type: actions.PAYMENT_SAGA_FAILED, payload: { isLoading: false, ...payload } };
}
function op(payload: any) {
  return { type: actions.PAYMENT_SLICE_OPERATION, payload: { isLoading: false, ...payload } };
}

// Start a Stripe HOSTED card-setup page (Checkout Session, mode:setup): the payer enters their
// card on Stripe's own page — Bookingo never touches card data. Returns the hosted URL; the
// setup_intent.succeeded webhook vaults the card + mandate. Guests have no Okta session, so we
// skip the token when there's no user.id (unauthorized = !user.id, per the accountPackage saga convention).
export function* createCheckoutSession(request: any): any {
  try {
    const { loggedInData, accountId, successUrl, cancelUrl, bindTo } = request.payload;
    const data = getLoggedInData(loggedInData);
    // `bindTo` names what this card is for; the webhook binds the new card to those only.
    const body: any = { accountId, successUrl, cancelUrl };
    if (bindTo) {
      body.bindSubscriptionIds = bindTo.subscriptionIds || [];
      body.bindBookingIds = bindTo.bookingIds || [];
    }
    const response = yield call(callApi, data, "post", "bookingoApi/stripe/checkout-session", body, !data.user?.id);
    if (response?.status === 200 && response.body.data?.url) {
      yield put(op({ operationType: "CHECKOUT_SESSION_READY", checkoutSessionUrl: response.body.data.url }));
    } else {
      notification("error", response?.body?.message || "Could not start secure card setup");
      yield put(fail());
    }
  } catch (err) {
    console.log("createCheckoutSession error", err);
    yield put(fail());
    bugsnagClient.notify(err as any);
  }
}

function* getPaymentMethods(request: any): any {
  try {
    const { loggedInData, accountId } = request.payload;
    const data = getLoggedInData(loggedInData);
    const response = yield call(callApi, data, "get", `bookingoApi/payment-method/account/${accountId}`, {}, !data.user?.id);
    if (response?.status === 200) {
      yield put(op({ operationType: "PAYMENT_METHODS_LOADED", paymentMethods: response.body.data || [], paymentMethodsAccountId: accountId }));
    } else {
      yield put(fail({ paymentMethods: [] }));
    }
  } catch (err) {
    console.log("getPaymentMethods error", err);
    yield put(fail({ paymentMethods: [] }));
    bugsnagClient.notify(err as any);
  }
}

// Turn on autopay for a booking's recurring pack + charge any outstanding invoice now.
function* enrollSubscription(request: any): any {
  try {
    const { loggedInData, ...body } = request.payload;
    const data = getLoggedInData(loggedInData);
    const response = yield call(callApi, data, "post", "bookingoApi/subscription/enroll", body, !data.user?.id);
    if (response?.status === 200 && response.body.data?.ok !== false) {
      yield put(op({ operationType: "ENROLLED", enrollResult: response.body.data }));
    } else {
      notification("error", response?.body?.data?.error || response?.body?.message || "Could not enable autopay");
      yield put(fail({ error: response?.body?.data?.error || response?.body?.message }));
    }
  } catch (err) {
    console.log("enrollSubscription error", err);
    yield put(fail());
    bugsnagClient.notify(err as any);
  }
}

function* getAccountSubscriptions(request: any): any {
  try {
    const { loggedInData, accountId } = request.payload;
    const data = getLoggedInData(loggedInData);
    const response = yield call(callApi, data, "get", `bookingoApi/subscription/account/${accountId}`, {}, !data.user?.id);
    if (response?.status === 200) {
      yield put(op({ operationType: "ACCOUNT_SUBS_LOADED", subscriptions: response.body.data || [], subscriptionsAccountId: accountId }));
    } else {
      yield put(fail({ subscriptions: [] }));
    }
  } catch (err) {
    console.log("getAccountSubscriptions error", err);
    yield put(fail({ subscriptions: [] }));
    bugsnagClient.notify(err as any);
  }
}

function* getNeedsAttention(request: any): any {
  try {
    const { loggedInData } = request.payload;
    const data = getLoggedInData(loggedInData);
    const response = yield call(callApi, data, "get", "bookingoApi/subscription/needs-attention", {}, !data.user?.id);
    if (response?.status === 200) {
      yield put(op({ operationType: "NEEDS_ATTENTION_LOADED", needsAttention: response.body.data || [] }));
    } else {
      yield put(fail({ needsAttention: [] }));
    }
  } catch (err) {
    console.log("getNeedsAttention error", err);
    yield put(fail({ needsAttention: [] }));
    bugsnagClient.notify(err as any);
  }
}

// { bookingId: autopayStatus } map for list badges (bookings + invoices lists).
function* getBookingAutopayMap(request: any): any {
  try {
    const { loggedInData } = request.payload;
    const data = getLoggedInData(loggedInData);
    const response = yield call(callApi, data, "get", "bookingoApi/subscription/booking-map", {}, !data.user?.id);
    if (response?.status === 200) {
      yield put(op({ operationType: "BOOKING_MAP_LOADED", bookingAutopayMap: response.body.data || {} }));
    } else {
      yield put(fail({ bookingAutopayMap: {} }));
    }
  } catch (err) {
    console.log("getBookingAutopayMap error", err);
    yield put(fail({ bookingAutopayMap: {} }));
    bugsnagClient.notify(err as any);
  }
}

// Staff "retry now" from the needs-attention screen — charges the outstanding invoice
// immediately, then refreshes the list so the row shows the new attempt / clears if paid.
function* retryAutopay(request: any): any {
  try {
    const { loggedInData, subscriptionId } = request.payload;
    const data = getLoggedInData(loggedInData);
    const response = yield call(callApi, data, "post", "bookingoApi/subscription/autopay/retry", { subscriptionId }, !data.user?.id);
    if (response?.status === 200 && response.body.data?.ok !== false) {
      notification("success", "Retrying the charge…");
      yield put(op({ operationType: "AUTOPAY_RETRIED" }));
      yield put({ type: actions.PAYMENT_GET_NEEDS_ATTENTION, payload: { loggedInData } });
    } else {
      notification("error", response?.body?.message || "Could not retry the charge");
      yield put(fail());
    }
  } catch (err) {
    console.log("retryAutopay error", err);
    yield put(fail());
    bugsnagClient.notify(err as any);
  }
}

// Ask a family to add a card for an enrolment that never got one. Staff-triggered; the backend
// refuses a second send on the same day, and the refusal is shown rather than swallowed so the
// front desk knows the family was already contacted today.
function* sendCardReminder(request: any): any {
  try {
    const { loggedInData, subscriptionId } = request.payload;
    const data = getLoggedInData(loggedInData);
    const response = yield call(callApi, data, "post", "bookingoApi/subscription/autopay/card-reminder", { subscriptionId }, !data.user?.id);
    if (response?.status === 200 && response.body.data?.ok !== false) {
      notification("success", "Reminder sent");
      yield put(op({ operationType: "CARD_REMINDER_SENT" }));
      yield put({ type: actions.PAYMENT_GET_NEEDS_ATTENTION, payload: { loggedInData } });
    } else {
      notification("error", response?.body?.message || "Could not send the reminder");
      yield put(fail());
    }
  } catch (err) {
    console.log("sendCardReminder error", err);
    yield put(fail());
    bugsnagClient.notify(err as any);
  }
}

// Apply a newly-vaulted card to the enrolments that need one. The scope is the payer's choice, so
// it travels with the request; the response reports what was changed and what was deliberately left
// alone, which the screen shows rather than making the family guess which card now pays for what.
function* replaceCard(request: any): any {
  try {
    const { loggedInData, accountId, paymentMethodId, subscriptionIds, applyToAll, consent } = request.payload;
    const data = getLoggedInData(loggedInData);
    const response = yield call(callApi, data, "post", "bookingoApi/subscription/replace-card",
      { accountId, paymentMethodId, subscriptionIds, applyToAll, consent }, !data.user?.id);
    if (response?.status === 200) {
      yield put(op({ operationType: "CARD_REPLACED", replaceCardResult: response.body.data }));
    } else {
      notification("error", response?.body?.message || "Could not apply the card");
      yield put(fail());
    }
  } catch (err) {
    console.log("replaceCard error", err);
    yield put(fail());
    bugsnagClient.notify(err as any);
  }
}

// Approve a cycle priced above what the payer last agreed to. The backend raises the mandate,
// releases the cycles held by the same change, and collects only those already due.
function* confirmIncrease(request: any): any {
  try {
    const { loggedInData, subscriptionId, invoiceId, confirmedBy } = request.payload;
    const data = getLoggedInData(loggedInData);
    const response = yield call(callApi, data, "post", "bookingoApi/subscription/autopay/confirm-increase",
      { subscriptionId, invoiceId, confirmedBy }, !data.user?.id);
    if (response?.status === 200) {
      yield put(op({ operationType: "INCREASE_CONFIRMED" }));
    } else {
      notification("error", response?.body?.message || "Could not approve the new amount");
      yield put(fail());
    }
  } catch (err) {
    console.log("confirmIncrease error", err);
    yield put(fail());
    bugsnagClient.notify(err as any);
  }
}

export default function* rootSaga() {
  yield takeLatest(actions.PAYMENT_CREATE_CHECKOUT_SESSION, createCheckoutSession);
  yield takeLatest(actions.PAYMENT_GET_METHODS, getPaymentMethods);
  yield takeLatest(actions.PAYMENT_ENROLL, enrollSubscription);
  yield takeLatest(actions.PAYMENT_GET_ACCOUNT_SUBS, getAccountSubscriptions);
  yield takeLatest(actions.PAYMENT_GET_NEEDS_ATTENTION, getNeedsAttention);
  yield takeLatest(actions.PAYMENT_GET_BOOKING_MAP, getBookingAutopayMap);
  yield takeLatest(actions.PAYMENT_RETRY_AUTOPAY, retryAutopay);
  yield takeLatest(actions.PAYMENT_SEND_CARD_REMINDER, sendCardReminder);
  yield takeLatest(actions.PAYMENT_REPLACE_CARD, replaceCard);
  yield takeLatest(actions.PAYMENT_CONFIRM_INCREASE, confirmIncrease);
}
